/* eslint-disable no-param-reassign */
import type { PaymentMethod } from '@lt/api/dist/methods/orders/types/paymentMethods';
import type {
  Bank,
  PaylateSuccess,
} from '@lt/api/dist/methods/payment/methods/paymentParams';
import type { PaymentType } from '@lt/api/dist/types/payment';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { CardUniqueInfo, PaymentStateType } from './types';

const initialState: PaymentStateType = {
  paymentMethodList: [],
  selectedMethod: null,
  paymentAmount: null,
  orderId: null,
  orderPin: null,
  paymentId: null,
  isPaid: false,
  uniqueInfo: {
    tariffs: [],
    code: null,
    redirectMobileURL: null,
    paylateData: null,
    isCardValid: false,
    cardInfo: {
      number: '',
      expMonth: '',
      expYear: '',
      cvv: '',
    },
    processorIdentifier: null,
    banks: [],
  },
  ofertaText: null,
};
export const name = 'payment';

const payment = createSlice({
  name,
  initialState,
  reducers: {
    setPaymentAmount(state, action: PayloadAction<number | null>) {
      state.paymentAmount = action.payload;
    },

    setPaymentMethod(state, action: PayloadAction<PaymentType>) {
      state.selectedMethod = action.payload;
    },
    disablePaymentMethod(state, action: PayloadAction<PaymentType>) {
      state.paymentMethodList = state.paymentMethodList.filter(
        (m) => m.type !== action.payload,
      );
    },
    setPaymentData(
      state,
      action: PayloadAction<{ methods: PaymentMethod[]; ofertaText: string }>,
    ) {
      const { methods, ofertaText } = action.payload;
      state.paymentMethodList = methods;
      state.ofertaText = ofertaText;

      methods.map((method) => {
        if (method.type === 't_bank_credit') {
          state.uniqueInfo.tariffs = method.additionalData;
          state.uniqueInfo.selectedTariffID ??= method.additionalData[0].id;
        }

        if (method.type === 't_bank_credit_flex') {
          state.uniqueInfo.code =
            method.additionalData.planTypes[0]?.code ?? null;
        }

        return method;
      });
    },

    setOrderInfo(
      state,
      action: PayloadAction<{
        orderId: number | null;
        pin: string | null;
        paymentId?: number;
      }>,
    ) {
      const {
        payload: { orderId, pin, paymentId },
      } = action;
      state.orderId = orderId;
      state.orderPin = pin;
      if (paymentId) state.paymentId = paymentId;
    },

    setCardDataAction(
      state,
      action: PayloadAction<{
        number: string;
        expMonth: string;
        expYear: string;
        cvv: string;
      }>,
    ) {
      state.uniqueInfo.cardInfo = action.payload;
    },
    setCardValidityAction(state, action: PayloadAction<boolean>) {
      state.uniqueInfo.isCardValid = action.payload;
    },

    set3dsData(state, action: PayloadAction<CardUniqueInfo['data3ds']>) {
      state.uniqueInfo.data3ds = action.payload;
    },

    openPay2meFrame(state, action: PayloadAction<string>) {
      state.uniqueInfo.pay2meURL = action.payload;
    },

    setPaylateData(state, action: PayloadAction<PaylateSuccess>) {
      state.uniqueInfo.paylateData = action.payload;
    },

    payCardSuccessed(
      state,
      action: PayloadAction<{
        paymentId: number;
        processorIdentifier: PaymentStateType['uniqueInfo']['processorIdentifier'];
      }>,
    ) {
      state.paymentId = action.payload.paymentId;
      state.uniqueInfo.processorIdentifier = action.payload.processorIdentifier;
    },

    finishPayment(state) {
      state.isPaid = true;
    },

    /**
     * Оплата ТБанк рассрочкой
     */
    setCurrentTariff(state, action: PayloadAction<string>) {
      state.uniqueInfo.selectedTariffID = action.payload;
    },

    /**
     * Оплата СБП
     */
    setExpiresIn: (
      state,
      action: PayloadAction<PaymentStateType['uniqueInfo']['expiresIn']>,
    ) => {
      state.uniqueInfo.expiresIn = action.payload;
    },
    setQRCodeURL: (state, action: PayloadAction<string>) => {
      state.uniqueInfo.qrURL = action.payload;
    },
    setRedirectMobileURL: (state, action: PayloadAction<string>) => {
      state.uniqueInfo.redirectMobileURL = action.payload;
    },
    setBanks: (state, action: PayloadAction<Bank[]>) => {
      state.uniqueInfo.banks = action.payload;
    },
  },
});

export const {
  setPaymentAmount,
  setPaymentMethod,
  setPaymentData,
  setOrderInfo,
  setCardDataAction,
  setCardValidityAction,
  set3dsData,
  payCardSuccessed,
  setPaylateData,
  finishPayment,
  openPay2meFrame,
  setCurrentTariff,
  setBanks,
  setQRCodeURL,
  setRedirectMobileURL,
  setExpiresIn,
  disablePaymentMethod,
} = payment.actions;

export default payment.reducer;
